/**
 * When to hand an uploaded file from its <video> to a shadow <audio>, and when to hand it back.
 *
 * The problem this exists for: Chrome on Android pauses a `<video>` that has a video track as
 * soon as the page is hidden or the screen locks. Only `<audio>` keeps going in the background —
 * which the TTS bar has been quietly proving since M3. So for the screen-off walk the same
 * stream is played by an `<audio>` element instead, and handed back on unlock.
 *
 * The rules live here, pure, because they are all edge cases and every one of them is a moment
 * where the learner's music either keeps playing or does not. `html5.ts` owns the elements and
 * does what this says.
 *
 * ONE CALLER RULE, load-bearing: feed this only the events of whichever element is currently
 * LIVE. During the shadow phase the <video> also emits `pause` — we paused it ourselves — and
 * routing that in would read as the learner stopping. The adapter drops non-live events instead
 * of asking this module to guess which element spoke.
 */

export type BackgroundMode = 'video' | 'audio';

/**
 * A `pause` this soon after the page went hidden is the browser doing it, not the learner —
 * they cannot reach the pause button through a locked screen. Generous on purpose: the pause
 * event lands some way after `visibilitychange`, and being late here means the audio never
 * starts, while being early costs nothing (a learner who paused *just* before locking gets a
 * shadow that is immediately paused too).
 */
export const BROWSER_PAUSE_GRACE_MS = 1500;

export interface BackgroundState {
  mode: BackgroundMode;
  /** What we believe about playback, from the events we have been handed. */
  playing: boolean;
  /** When the page went hidden, or null while it is visible. */
  hiddenSince: number | null;
}

export type BackgroundEvent =
  | { type: 'hidden'; at: number }
  | { type: 'visible'; at: number }
  | { type: 'play'; at: number }
  | { type: 'pause'; at: number }
  | { type: 'ended'; at: number };

/**
 * `startAudio` — create/resume the shadow and make it the clock.
 * `stopAudio`  — realign the video to the shadow's time and drop it.
 */
export type BackgroundAction = 'none' | 'startAudio' | 'stopAudio';

export function initialBackgroundState(): BackgroundState {
  return { mode: 'video', playing: false, hiddenSince: null };
}

export function backgroundReduce(
  state: BackgroundState,
  event: BackgroundEvent,
): { state: BackgroundState; action: BackgroundAction } {
  const still = (next: Partial<BackgroundState>): { state: BackgroundState; action: 'none' } => ({
    state: { ...state, ...next },
    action: 'none',
  });

  switch (event.type) {
    case 'hidden': {
      const hidden = { ...state, hiddenSince: event.at };
      // Only hand off something that is actually running. Pausing and pocketing the phone must
      // not start audio the learner did not ask for.
      if (state.mode === 'video' && state.playing)
        return { state: { ...hidden, mode: 'audio' }, action: 'startAudio' };
      return { state: hidden, action: 'none' };
    }

    case 'visible': {
      const visible = { ...state, hiddenSince: null };
      // NOTE: `playing` is carried over unchanged on purpose — whether the video actually
      // resumed depends on the shadow (a shadow that never loaded is left paused), which only
      // the adapter can see. It reconciles `playing` from the real elements right after acting
      // on `stopAudio`; believing a stale `true` here is what would let the NEXT lock start
      // audio over a video the learner is looking at stopped.
      if (state.mode === 'audio')
        return { state: { ...visible, mode: 'video' }, action: 'stopAudio' };
      return { state: visible, action: 'none' };
    }

    case 'play': {
      // ▶ from the lock screen while the video still holds the stream: the video would be paused
      // again the instant it started, so this is a hand-off too.
      if (state.mode === 'video' && state.hiddenSince !== null)
        return { state: { ...state, mode: 'audio', playing: true }, action: 'startAudio' };
      return still({ playing: true });
    }

    case 'pause': {
      // The browser's own pause, arriving just after the screen locked — not the learner's, who
      // cannot reach a button through a locked screen.
      //
      // Deliberately NOT conditioned on `state.playing`. This trigger exists precisely for the
      // case where `hidden` believed nothing was playing — the element had already been stopped
      // by the browser, or we missed its `play` — and requiring `playing` here would make the
      // whole branch unreachable, since `hidden` already hands off whenever we know we are
      // playing. A pause landing inside the window is evidence that something *was* running.
      const browserPause =
        state.hiddenSince !== null && event.at - state.hiddenSince <= BROWSER_PAUSE_GRACE_MS;
      if (state.mode === 'video' && browserPause)
        return { state: { ...state, mode: 'audio', playing: true }, action: 'startAudio' };
      // Otherwise it is a real pause: the learner's, or ours on the audio. Either way, stopped.
      return still({ playing: false });
    }

    case 'ended': {
      // Nothing left to keep alive in the background; give the video its stream back so a replay
      // starts from a normal state.
      if (state.mode === 'audio')
        return { state: { ...state, mode: 'video', playing: false }, action: 'stopAudio' };
      return still({ playing: false });
    }
  }
}
